import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AdminService } from '../../services/admin.service';
import { AuthService } from '../../auth.service';
import { OnInit } from '@angular/core';
type UploadState = 'idle' | 'loading' | 'success' | 'error';
type RemoveState = 'idle' | 'confirm' | 'removing' | 'removed';
export type ErrorType = 'not-image' | 'too-large' | 'no-file' | 'unexpected' | null;

const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const PLACEHOLDER = 'assets/img/theme/no-profile-placeholder.png';  

@Component({ standalone: false,
  selector: 'app-profile-photo-modal',
  templateUrl: './profile-photo-modal.component.html',
  styleUrls: ['./profile-photo-modal.component.scss']
})
export class ProfilePhotoModalComponent implements OnInit {
  @Input()  profilePhotoUrl: string | null = null;
  @Output() closed       = new EventEmitter<void>();
  @Output() photoUpdated = new EventEmitter<string>();

  uploadState: UploadState = 'idle';
  errorType: ErrorType = null;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isDragging = false;
  selectedFileName = '';
  selectedFileSizeMB = '';
  hasRealPhoto = false;
  removeState: RemoveState = 'idle';  

  readonly placeholder = PLACEHOLDER;  

  constructor(
    private adminService: AdminService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.hasRealPhoto = !!this.profilePhotoUrl && this.profilePhotoUrl !== PLACEHOLDER;
  }

  close(): void {
    if (this.uploadState === 'loading' || this.removeState === 'removing') return;
    this._reset();
    this.closed.emit();
  }

  onFileSelected(event: Event & { target: HTMLInputElement }): void {
    const f = event.target.files?.[0];
    if (f) this._setFile(f);
  }

  removeSelectedFile(): void {
    // Only reset upload-related state, don't touch removeState
    this.uploadState        = 'idle';
    this.errorType          = null;
    this.selectedFile       = null;
    this.previewUrl         = null;
    this.selectedFileName   = '';
    this.selectedFileSizeMB = '';
    this.isDragging         = false;

    const input = document.getElementById('photoFileInput') as HTMLInputElement;
    if (input) input.value = '';
  }

  onDragOver(e: DragEvent): void  { e.preventDefault(); this.isDragging = true; }
  onDragLeave(_: DragEvent): void { this.isDragging = false; }
  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.isDragging = false;
    const f = e.dataTransfer?.files?.[0];
    if (f) this._setFile(f);
  }

  isUploadDisabled(): boolean {
    return !this.selectedFile || this.uploadState === 'loading';
  }

  // ─── NEW: Remove-photo flow ───────────────────────────────────────────────

  /** Called by the "Remove" button (trash icon) next to Choose File */
  askRemove(): void {
    this.removeState = 'confirm';
  }

  /** User clicked "Keep" in the confirmation row */
  keepPhoto(): void {
    this.removeState = 'idle';
  }

  /** User clicked "Remove" in the confirmation row */
  confirmRemove(): void {
  this.removeState = 'removing';

  const token = this.authService.getToken();
  fetch(PLACEHOLDER)
    .then(r => r.blob())
    .then(blob => {
      const placeholderFile = new File([blob], 'placeholder.png', { type: 'image/png' });
      this.adminService.uploadUserImage(token, placeholderFile).subscribe({
        next: (res) => {
          const newUrl = (res?.result as any)?.imageUrl ?? PLACEHOLDER;
          this.authService.updateSession({
            userDetails: {
              ...(this.authService.getSession()?.userDetails ?? {}),
              imageUrl: newUrl
            }
          });
          this._onSuccess(newUrl, true);  // ← pass true
        },
        error: () => {
          this.profilePhotoUrl = PLACEHOLDER;
          this._onSuccess(PLACEHOLDER, true);  // ← pass true
        }
      });
    });
}

  // ─────────────────────────────────────────────────────────────────────────

  upload(): void {
    if (!this.selectedFile) {
      this.errorType = 'no-file';
      this.uploadState = 'error';
      return;
    }
    if (this.uploadState === 'loading') return;

    this.uploadState = 'loading';
    this.errorType = null;

    const token = this.authService.getToken();
    this.adminService.uploadUserImage(token, this.selectedFile).subscribe({
      next: (res) => {
        const newUrl = (res?.result as any)?.imageUrl ?? this.previewUrl;
        this.authService.updateSession({
          userDetails: {
            ...(this.authService.getSession()?.userDetails ?? {}),
            imageUrl: newUrl
          }
        });
        this._onSuccess(newUrl);
      },
      error: (err) => this._onError(err)
    });
  }

  private _onSuccess(url: string, fromRemove = false): void {
    this.profilePhotoUrl = url;
    this.uploadState     = fromRemove ? 'idle' : 'success';  // ← key change
    this.selectedFile    = null;
    this.previewUrl      = null;
    this.removeState     = fromRemove ? 'removed' : 'idle';  // ← keep 'removed' state
    this.hasRealPhoto    = !fromRemove;
    this.photoUpdated.emit(url);
  }

  private _onError(err: any): void {
    console.error('Upload failed', err);
    this.uploadState = 'error';
    this.errorType = 'unexpected';
  }

  private _setFile(file: File): void {
    if (!ALLOWED_TYPES.includes(file.type)) {
      this.errorType   = 'not-image';
      this.uploadState = 'error';
      this.selectedFile = null;
      this.previewUrl   = null;
      this.removeState  = 'idle';  // ← reset so error zone shows
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_SIZE_MB) {
      this.errorType   = 'too-large';
      this.uploadState = 'error';
      this.selectedFile = null;
      this.previewUrl   = null;
      this.removeState  = 'idle';  // ← reset so error zone shows
      return;
    }

    this.errorType          = null;
    this.uploadState        = 'idle';
    this.removeState        = 'idle';  // ← always reset when valid file picked
    this.selectedFileName   = file.name;
    this.selectedFileSizeMB = sizeMB.toFixed(1);
    this.selectedFile       = file;

    const reader = new FileReader();
    reader.onload = (e) => this.previewUrl = e.target?.result as string;
    reader.readAsDataURL(file);
  }

  private _reset(): void {
    this.uploadState        = 'idle';
    this.errorType          = null;
    this.selectedFile       = null;
    this.previewUrl         = null;
    this.selectedFileName   = '';
    this.selectedFileSizeMB = '';
    this.isDragging         = false;
    this.removeState        = 'idle';  
  }

  get canRemovePhoto(): boolean {
    return this.hasRealPhoto 
      && !this.selectedFile 
      && !this.previewUrl
      && this.removeState === 'idle'
      && this.uploadState !== 'loading';
  }
}